import i18n from '@/i18n';
import { dateTimeFormats } from '@/lang/dateTimeFormats';
import {
  leaveEndDate,
  leaveStartDate,
} from '@/lib/leave/leaveRequestFunctions';
import { ShiftAreaSessionFormItemType } from '@/types/shiftSessionsForm';
import { addDays, isPast } from '@/util/dateArithmetic';
import { setTime } from '@/util/dateFunctions';
import { getEntityName } from '@/util/entityFunctions';
import spacetime from 'spacetime';
import {
  Employee,
  JobRole,
  LeaveRequest,
  ScheduledBreak,
  Shift,
  ShiftTemplate,
  ShiftTemplateBreak,
  ShiftTemplateShiftAreaSession,
  Unavailability,
} from '../../../api/v1';

export const convertShiftTemplateTimesToDates = (
  date: Date,
  template: ShiftTemplate,
  timezone: string,
): { startsAt: Date; endsAt: Date; showEndTime: boolean } => {
  const startsAt = setTime(
    date,
    timezone,
    template.startHour,
    template.startMinute,
  );
  let endsAt = setTime(date, timezone, template.endHour, template.endMinute);
  if (endsAt <= startsAt) {
    endsAt = addDays(endsAt, 1, timezone);
  }
  return { startsAt, endsAt, showEndTime: template.showEndTime };
};

export const convertShiftTemplateBreakToScheduledBreak = (
  shiftStartsAt: Date,
  templateBreak: ShiftTemplateBreak,
  timezone: string,
  id: number = null,
): Omit<ScheduledBreak, 'companyId' | 'shiftId' | 'comparisonHash'> => {
  let breakStartsAt =
    templateBreak.startHour !== null && templateBreak.startMinute !== null
      ? setTime(
          shiftStartsAt,
          timezone,
          templateBreak.startHour,
          templateBreak.startMinute,
        )
      : null;
  if (breakStartsAt) {
    const isNextDayBreak = spacetime(shiftStartsAt, timezone).isAfter(
      breakStartsAt,
    );
    if (isNextDayBreak) {
      breakStartsAt = addDays(breakStartsAt, 1, timezone);
    }
  }
  return {
    id,
    startsAt: breakStartsAt,
    durationInMinutes: templateBreak.durationInMinutes,
    paid: templateBreak.paid,
  };
};

export const convertShiftTemplateAreaSessionToShiftAreaSession = (
  shiftStartsAt: Date,
  templateAreaSession: ShiftTemplateShiftAreaSession,
  timezone: string,
  id: number = null,
): ShiftAreaSessionFormItemType => {
  let startTime = setTime(
    shiftStartsAt,
    timezone,
    templateAreaSession.startHour,
    templateAreaSession.startMinute,
  );
  if (spacetime(shiftStartsAt, timezone).isAfter(startTime)) {
    startTime = addDays(startTime, 1, timezone);
  }

  let endTime = setTime(
    startTime,
    timezone,
    templateAreaSession.endHour,
    templateAreaSession.endMinute,
  );
  if (spacetime(startTime, timezone).isAfter(endTime)) {
    endTime = addDays(endTime, 1, timezone);
  }

  return {
    id,
    shiftAreaId: templateAreaSession.shiftAreaId,
    startTime,
    endTime,
  };
};

export const convertShiftAreaSessionToShiftTemplateAreaSession = (
  shiftAreaSession: ShiftAreaSessionFormItemType,
  timezone: string,
): {
  shiftAreaId: number;
  startHour: number;
  startMinute: number;
  endHour: number;
  endMinute: number;
} => ({
  shiftAreaId: shiftAreaSession.shiftAreaId,
  startHour: spacetime(shiftAreaSession.startTime, timezone).hour(),
  startMinute: spacetime(shiftAreaSession.startTime, timezone).minute(),
  endHour: spacetime(shiftAreaSession.endTime, timezone).hour(),
  endMinute: spacetime(shiftAreaSession.endTime, timezone).minute(),
});

export const shiftTemplateDisabledForEmployee = (
  template: ShiftTemplate,
  startDate: Date,
  employee: Employee,
  unavailability: Unavailability[],
  leaveRequests: LeaveRequest[],
  shifts: Shift[],
  timezone: string,
): string => {
  const { startsAt, endsAt } = convertShiftTemplateTimesToDates(
    startDate,
    template,
    timezone,
  );

  if (isPast(startsAt)) {
    return i18n.tc('shiftTemplate.disabled.templateStartedInPast');
  }

  if (employee) {
    const { jobRole } = template;
    const employeeHasJobRole = jobRole
      ? employee.jobRoleIds.includes(jobRole.id)
      : true;
    if (!employeeHasJobRole) {
      return i18n.tc('schedule.error.employeeWrongJobRole', 1, {
        employee: employee.firstName,
      });
    }

    const employeeUnavailability = unavailability.find(
      (u) => u.endsAt > startsAt && u.startsAt < endsAt,
    );

    if (employeeUnavailability) {
      return i18n.tc('schedule.error.employeeHasUnavailability', 1, {
        employee: employee.firstName,
      });
    }

    const leave = leaveRequests.find(
      (l) =>
        leaveStartDate(l, timezone) < endsAt &&
        leaveEndDate(l, timezone) > startsAt,
    );
    if (leave) {
      return i18n.tc('schedule.error.employeeHasLeave', 1, {
        employee: employee.firstName,
      });
    }

    const employeeShift = shifts.find(
      (s) => s.endsAt > startsAt && s.startsAt < endsAt,
    );
    if (employeeShift) {
      return i18n.tc('schedule.error.employeeHasShift', 1, {
        employee: employee.firstName,
      });
    }
  }

  return '';
};

export const generateTemplateName = (
  {
    jobRoleId,
    startsAt,
    endsAt,
  }: { jobRoleId: number; startsAt: Date; endsAt: Date },
  jobRoles: JobRole[],
  shiftTemplates: ShiftTemplate[],
): string => {
  const templateName = `${getEntityName(
    jobRoleId,
    jobRoles,
    'New template',
  )} (${i18n.d(startsAt, dateTimeFormats.hourMinute)} - ${i18n.d(
    endsAt,
    dateTimeFormats.hourMinute,
  )})`;

  const templatesWithName = shiftTemplates.filter(({ name }) =>
    name.startsWith(templateName),
  );

  return templatesWithName.length
    ? `${templateName} ${templatesWithName.length}`
    : templateName;
};

export const formatTimeShiftTemplateDialog = (
  hour: number,
  minute: number,
  timezone: string = 'Europe/London',
): string => {
  const date = setTime(new Date(), timezone, hour, minute);
  return i18n.d(date, dateTimeFormats.hourMinute);
};
